import React, { useEffect, useState, useRef, useContext } from "react";
import {
  Alert,
  Button,
  Col,
  Form,
  InputNumber,
  Modal,
  Row,
  Spin,
  message,
  Select,
  TimePicker,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import SimpleReactValidator from "simple-react-validator";
import ProtocolServices from "../../services/ProtocolServices";
import { translate } from "../../locale";
import dayjs from "dayjs";
import AddPill from "./AddPill";
import { WindowContext } from "../../providers/WindowContext";
import { AdminContext } from "../../providers/AdminContext";
import PropTypes from "prop-types";

const { Option } = Select;

const SimpleReactValidatorMessage = (message) => (
  <div className="text-red-600 pt-2 text-xs">{message}</div>
);

const AddProtocolDay = ({ protocolId, visible, onComplete, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [loadingSelect, setLoadingSelect] = useState(false);
  const [surveySelect, setSurveySelect] = useState(null);
  const [pillSelect, setPillSelect] = useState(null);
  const [active, setActive] = useState(false);
  const [showAddPill, setShowAddPill] = useState(false);
  const [values, setValues] = useState({
    day: "",
    type: "",
    form: "",
    pill: "",
    rpm: "",
    flag_block: false,
    persist: "",
    dispatch_time: dayjs("1970-01-01 12:00", "HH:mm"),
    feeding_day_food: "",
  });
  const [error, setError] = useState("");
  const [, setForceUpdate] = useState();
  const { user } = useContext(AdminContext);
  const [isSuper, setIsSuper] = useState(false);
  const [rpmSelect, setRpmSelect] = useState(null);
  const [recurrentOptionsModal, setRecurrentOptionsModal] = useState(false);
  const [timeToRepeat, setTimeToRepeat] = useState(0);

  const [dayFoodSelect, setDayFoodSelect] = useState(null);
  const endRepeatOptions = [{ label: "Después", value: "after" }];
  const blockingOptions = [
    { label: "Normal", value: "" },
    { label: "Bloqueo de protocolo", value: "flag_block" },
    { label: "Persistente", value: "flag_persistent" },
  ];
  const [showPersistentDays, setShowPersistentDays] = useState(false);

  const [recurrentOptionsValues, setRecurrentOptionsValues] = useState({
    repeat: null,
    endOfRepeat: null,
  });

  const simpleValidator = useRef(
    new SimpleReactValidator({
      autoForceUpdate: { forceUpdate: setForceUpdate },
      element: SimpleReactValidatorMessage,
      messages: {
        required: "Campo requerido",
      },
    })
  );
  const { windowIsActive } = useContext(WindowContext);
  useEffect(() => {
    if (visible !== null) {
      setActive(visible);
      if (visible === true) {
        if (windowIsActive && values.type === "form") {
          loadSurveys();
        }
      }
    }
  }, [visible, windowIsActive, values.type]);

  useEffect(() => {
    if (user !== null && user !== undefined) {
      setIsSuper(user.user.rol === "eira-super-admin");
    }
  }, [user]);

  const disabled = () => !simpleValidator.current.allValid();

  const onFinish = async () => {
    setLoading(true);
    setError("");
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      setForceUpdate();
      setError("Todos los datos son requeridos");
      return;
    }
    try {
      const data = {
        protocol: parseInt(protocolId),
        protocol_details: [
          {
            day: parseInt(values.day),
            type: values.type,
            ...(values.type === "form" && { form: values.form }),
            ...(values.type === "pill" && { pill: values.pill }),
            ...(values.type === "rpm" && { record: values.rpm }),
            ...(values.feeding_day_food !== "" && {
              feeding_day_food: values.feeding_day_food,
            }),
            flag_block: values.flag_block,
            conditions: [],
            dispatch_time: values.dispatch_time.format("HH:mm:ss"),
            frequency: recurrentOptionsValues.repeat
              ? recurrentOptionsValues.repeat
              : 0,
            after: timeToRepeat !== 0 ? timeToRepeat : 1,
            ...(values.persist && { persist: values.persist }),
          },
        ],
      };

      const res = await ProtocolServices.saveProtocolDetails(data);
      if (res.status >= 200 && res.status < 300) {
        message.success("El día del protocolo se guardó exitosamente.");
        resetForm();
        if (onComplete) {
          onComplete();
        }
      } else {
        setError(translate("Error al guardar la acción."));
      }
      setLoading(false);
    } catch (e) {
      console.log(e);
      setError(e.message);
      setLoading(false);
    }
  };

  const resetForm = () => {
    setLoading(false);
    setValues({
      day: "",
      type: "",
      form: "",
      pill: "",
      rpm: "",
      flag_block: false,
      dispatch_time: dayjs("1970-01-01 12:00", "HH:mm"),
      feeding_day_food: "",
      persist: "",
    });
    setTimeToRepeat(0);
    setSurveySelect(null);
    setPillSelect(null);
    setRpmSelect(null);
    setDayFoodSelect(null);
    simpleValidator.current.hideMessages();
    simpleValidator.current.visibleFields = [];
    setRecurrentOptionsValues({ repeat: null, endOfRepeat: null });
    setForceUpdate();
  };

  const loadSurveys = async () => {
    setLoadingSelect(true);
    try {
      let data = {
        page_size: 1000,
        page_num: 1,
      };
      const res = await ProtocolServices.getSurveys(data);
      if (res.status >= 200 && res.status < 300) {
        setSurveySelect(res.data.survey);
      }
      setLoadingSelect(false);
    } catch (error) {
      console.log(error);
      setLoadingSelect(false);
    }
  };

  const loadPills = async () => {
    setLoadingSelect(true);
    try {
      let data = {
        page_size: 10000,
        page_num: 1,
      };
      const res = await ProtocolServices.getPill(data);
      if (res.status >= 200 && res.status < 300) {
        setPillSelect(res.data.pills);
      }
      setLoadingSelect(false);
    } catch (error) {
      console.log(error);
      setLoadingSelect(false);
    }
  };

  const handleChange = (name, value) => {
    simpleValidator.current.purgeFields();
    if (name === "type") {
      setValues((v) => ({ ...v, form: "", pill: "", rpm: "" }));
      setSurveySelect(null);
      setPillSelect(null);
      setRpmSelect(null);
      setDayFoodSelect(null);
      switch (value) {
        case "form":
          break;
        case "pill":
          loadPills();
          break;
        case "jumpto":
          break;
        case "rpm":
          setRpmSelect([
            { id: "diabetes", name: "Glucemia" },
            { id: "hypertension", name: "Presión arterial" },
            { id: "weight", name: "Peso" },
            { id: "feeding", name: "Alimentación" },
            { id: "o2saturation", name: "Saturación O2" },
            { id: "temperature", name: "Temperatura" },
            { id: "water", name: "Agua" },
            { id: "physical_activity", name: "Actividad física" },
          ]);
          break;
      }
    } else if (name === "rpm") {
      setDayFoodSelect(null);
      if (value === "feeding") {
        setDayFoodSelect([
          { name: "Desayuno", id: "desayuno" },
          { name: "Almuerzo", id: "almuerzo" },
          { name: "Merienda", id: "merienda" },
          { name: "Cena", id: "cena" },
          { name: "Colación", id: "colacion" },
        ]);
      }
    }

    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    simpleValidator.current.showMessageFor(name);
    setValues((v) => ({ ...v, [name]: value }));
  };

  const handleCancel = () => {
    resetForm();

    setActive(false);
    if (onCancel) {
      onCancel();
    }
  };
  const handleOnCompletePill = (data) => {
    setPillSelect((v) => {
      let arr = v;
      arr.push(data);
      return arr;
    });
    setValues((v) => ({ ...v, pill: data.id }));
    setShowAddPill(false);
  };

  const handleOnCancelPill = () => {
    setShowAddPill(false);
  };

  const goToAddForm = () => {
    window.open(process.env.REACT_APP_SURVEY_CREATOR_URL + "/login");
  };

  const handleBlockTypeChange = (value) => {
    setValues((v) => ({
      ...v,
      flag_block: value === "flag_block",
      persist: value === "flag_persistent" ? 1 : null,
    }));
    if (value === "flag_persistent") {
      setShowPersistentDays(true);
    } else {
      setShowPersistentDays(false);
    }
  };

  const handleFilterPill = (input, option) => {
    const rawInput = (input || "").trim();
    const isNumeric = /^[0-9]+$/.test(rawInput);
    const inputValue = rawInput.toLowerCase();
    const labelText = (option.children || "").toString().toLowerCase();
    const capsuleId = (option["data-capsule"] || "").toString().toLowerCase();
    if (isNumeric) {
      return capsuleId == inputValue;
    }
    return labelText.includes(inputValue);
  };

  return (
    <>
      <Modal
        open={active}
        title={translate("Agregar acción")}
        onOk={null}
        onCancel={handleCancel}
        footer={false}
      >
        {error && (
          <Alert
            message={error}
            type="error"
            className="mb-6"
            closable
            onClose={() => setError("")}
          />
        )}
        <Form
          initialValues={{ remember: true }}
          onFinish={onFinish}
          autoComplete="off"
          className="w-full"
        >
          <Form.Item>
            <InputNumber
              placeholder="Día"
              className="border-custom_primary"
              name="day"
              min={1}
              onChange={(v) => handleChange("day", v)}
              onBlur={handleBlur}
              value={values.day}
            />
            {simpleValidator.current.message(
              "day",
              values.day,
              "required|min:1,num"
            )}
          </Form.Item>

          <Form.Item>
            <div>{translate("Hora de envío de notificaciones")}</div>
            <TimePicker
              format="HH:mm"
              placeholder="00:00"
              className="border-custom_primary"
              name="dispatch_time"
              defaultValue={dayjs("1970-01-01 12:00", "HH:mm")}
              onChange={(time) => handleChange("dispatch_time", time)}
              value={values.dispatch_time}
              minuteStep={10}
            />
            {simpleValidator.current.message(
              "dispatch_time",
              values.dispatch_time !== null
                ? values.dispatch_time.toString()
                : "",
              "required"
            )}
          </Form.Item>
          <Form.Item>
            <Select
              name="type"
              placeholder={translate("Seleccione tipo")}
              onChange={(v) => handleChange("type", v)}
              value={values.type}
            >
              <Option value="">{translate("Seleccione tipo")}</Option>
              <Option value="form">{translate("Formulario")}</Option>
              <Option value="pill">{translate("Píldora")}</Option>
              <Option value="rpm">{translate("Registro")}</Option>
              <Option value="telemedicine">{translate("Telemedicina")}</Option>
            </Select>
            {simpleValidator.current.message("type", values.type, "required")}
          </Form.Item>

          {surveySelect !== null && (
            <Form.Item>
              <div className="flex flex-row space-x-4">
                <Select
                  showSearch
                  name="form"
                  placeholder={translate("Seleccione formulario")}
                  onChange={(v) => handleChange("form", v)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {surveySelect.map((survey) => (
                    <Option key={survey.id} value={survey.id}>
                      {survey.title}
                    </Option>
                  ))}
                </Select>
                <Button
                  type="primary"
                  className="bg-custom_primary"
                  onClick={() => goToAddForm()}
                >
                  +
                </Button>
              </div>
              {simpleValidator.current.message("form", values.form, "required")}
            </Form.Item>
          )}

          {pillSelect !== null && (
            <Form.Item>
              <div className="flex flex-row space-x-4">
                <Select
                  className="flex-1"
                  showSearch
                  name="pill"
                  placeholder={translate("Seleccione píldora")}
                  onChange={(v) => handleChange("pill", v)}
                  filterOption={handleFilterPill}
                  value={values.pill}
                >
                  <Option value="">{translate("Seleccione píldora")}</Option>
                  {pillSelect.map((pill) => (
                    <Option
                      key={pill.id}
                      value={pill.id}
                      data-capsule={pill.capsule_strapi_id || ""}
                    >
                      {`${
                        pill.enterprises_info[0].name
                          ? `${pill.enterprises_info[0].name} / `
                          : ""
                      }${pill.name}`}
                    </Option>
                  ))}
                </Select>
                {isSuper && (
                  <Button
                    type="primary"
                    className="bg-custom_primary"
                    onClick={() => setShowAddPill(true)}
                  >
                    +
                  </Button>
                )}
              </div>
              {simpleValidator.current.message("pill", values.pill, "required")}
            </Form.Item>
          )}

          {rpmSelect !== null && (
            <Form.Item>
              <div className="flex flex-row space-x-4">
                <Select
                  className="flex-1"
                  showSearch
                  name="rpm"
                  placeholder={translate("Seleccione registro")}
                  onChange={(v) => handleChange("rpm", v)}
                  filterOption={(input, option) => {
                    return option.children
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                  value={values.rpm}
                >
                  <Option value="">{translate("Seleccione registro")}</Option>
                  {rpmSelect.map((rpm) => (
                    <Option key={rpm.id} value={rpm.id}>
                      {rpm.name}
                    </Option>
                  ))}
                </Select>
              </div>
              {simpleValidator.current.message("rpm", values.rpm, "required")}
            </Form.Item>
          )}

          {dayFoodSelect !== null && (
            <Form.Item>
              <div className="flex flex-row space-x-4">
                <Select
                  className="flex-1"
                  showSearch
                  name="feeding_day_food"
                  placeholder={translate("Seleccione alimento")}
                  onChange={(v) => handleChange("feeding_day_food", v)}
                  filterOption={(input, option) => {
                    return option.children
                      .toLowerCase()
                      .includes(input.toLowerCase());
                  }}
                  value={values.feeding_day_food}
                >
                  <Option value="">{translate("Seleccione alimento")}</Option>
                  {dayFoodSelect.map((feeding_day_food) => (
                    <Option
                      key={feeding_day_food.id}
                      value={feeding_day_food.id}
                    >
                      {feeding_day_food.name}
                    </Option>
                  ))}
                </Select>
              </div>
              {simpleValidator.current.message(
                "feeding_day_food",
                values.feeding_day_food,
                "required"
              )}
            </Form.Item>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item>
                <div>{translate("Estado de la acción")}</div>
                <Select
                  defaultValue=""
                  disabled={values.type === "pill"}
                  options={blockingOptions}
                  onChange={handleBlockTypeChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              {showPersistentDays && (
                <Form.Item>
                  <div>{translate("Persistir durante")}</div>
                  <Select
                    defaultValue={1}
                    disabled={values.type === "pill"}
                    options={Array.from({ length: 30 }, (_, i) => i + 1).map(
                      (i) => ({
                        value: i,
                        label: `${i} ${
                          i === 1 ? translate("día") : translate("días")
                        }`,
                      })
                    )}
                    onChange={(v) => handleChange("persist", v)}
                  />
                </Form.Item>
              )}
            </Col>
          </Row>

          {loadingSelect && (
            <Form.Item className="text-center">
              <Spin indicator={<LoadingOutlined />} />
            </Form.Item>
          )}
          <Form.Item>
            <Button
              type="primary"
              className="bg-custom_primary"
              onClick={() => {
                setRecurrentOptionsModal(true);
              }}
            >
              Es recurrente
            </Button>
          </Form.Item>

          <div className="mb-2">
            {recurrentOptionsValues.repeat &&
              recurrentOptionsValues.repeat !== "Ninguna" &&
              `Se repite cada ${recurrentOptionsValues.repeat} días.`}
            {timeToRepeat !== 0 && ` Termina luego de ${timeToRepeat} veces`}
          </div>

          <Form.Item>
            <Row align="end" gutter={16}>
              <Col>
                <Button key="back" onClick={handleCancel}>
                  {translate("Cancelar")}
                </Button>
              </Col>
              <Col>
                <Button
                  disabled={disabled()}
                  htmlType="submit"
                  type="primary"
                  className="bg-custom_primary"
                >
                  {loading ? (
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ color: "#666666" }} />
                      }
                    />
                  ) : (
                    "Guardar"
                  )}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Modal>
      <AddPill
        visible={showAddPill}
        onComplete={handleOnCompletePill}
        onCancel={handleOnCancelPill}
      />

      <Modal
        open={recurrentOptionsModal}
        onCancel={() => {
          setRecurrentOptionsModal(false);
          setRecurrentOptionsValues({ repeat: null, endOfRepeat: null });
          setTimeToRepeat(0);
        }}
        title={translate("Configurar recurrencia")}
        onOk={() => setRecurrentOptionsModal(false)}
      >
        <Form>
          <Form.Item>
            <div>Repetir cada</div>
            <div className="flex flex-row space-x-4">
              <InputNumber
                className="flex-1"
                value={recurrentOptionsValues.repeat}
                placeholder={"0 días"}
                onChange={(value) => {
                  setRecurrentOptionsValues({
                    ...recurrentOptionsValues,
                    repeat: value,
                  });
                }}
              />
            </div>
          </Form.Item>
          <Form.Item>
            <div>La repeticion termina</div>
            <Select
              className="flex-1"
              placeholder={translate("Seleccione una opcion")}
              value={recurrentOptionsValues.endOfRepeat}
              onChange={(value) =>
                setRecurrentOptionsValues({
                  ...recurrentOptionsValues,
                  endOfRepeat: value,
                })
              }
            >
              {endRepeatOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <div className="text-md">La repetición termina luego de</div>

          <div className="flex flex-row items-center">
            <Form.Item className="flex flex-row space-x-4">
              <InputNumber
                placeholder="0"
                min={1}
                value={timeToRepeat === 0 ? null : timeToRepeat}
                onChange={(value) => setTimeToRepeat(value)}
                disabled={!recurrentOptionsValues.endOfRepeat}
              />
              <span className="ml-4 text-center">veces</span>
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
};

AddProtocolDay.propTypes = {
  protocolId: PropTypes.string,
  visible: PropTypes.bool,
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
};

export default AddProtocolDay;
